/**
 * @fileoverview Renovation Project Entity Configuration
 *
 * Complete configuration for renovation projects compatible with UniversalDetailPage.
 * Defines all fields, API endpoints, and custom behaviors for project management.
 *
 * @module RenovationProject/EntityConfig
 */

import { UniversalEntityConfig } from "shared/types/universalEntityConfig";
import {
  Buildings,
  Calendar,
  Coins,
  PencilSimple,
  User,
  SquaresFour,
  CalendarDot,
} from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { applyMask } from "shared/utils/masks";
import { ResponsibleUserDisplay } from "../components/ResponsibleUserDisplay";
import { ProjectProvisionalUnitsCell } from "../customSections/ProjectProvisionalUnitsCell";

export const RENOVATION_PROJECT_ENTITY_CONFIG: UniversalEntityConfig = {
  // ============ Basic Identity ============
  entityName: "renovation-project",
  displayName: "Renovatieproject",
  displayNamePlural: "Renovatieprojecten",
  entityPrefix: "RENO-",

  // ============ API Configuration ============
  api: {
    fetchOne: (id) => `${API_URL}/renovation/project/${id}`,
    fetchAll: () => `${API_URL}/renovation/project`,
    update: (id) => `${API_URL}/renovation/project/${id}`,
    delete: (id) => `${API_URL}/renovation/project/${id}/comprehensive`,
    create: () => `${API_URL}/renovation/project`,
  },

  // ============ Fields ============
  fields: [
    // Title
    {
      id: "title",
      label: "Titel",
      type: "text",
      icon: PencilSimple,
      getValue: (project) => project?.title || "",
      formatDisplay: (value) => value || "-",
      buildUpdatePayload: (value) => ({ title: value }),
      detailConfig: {
        editable: true,
        required: true,
        placeholder: "Voer project titel in",
        section: "main",
        validate: (value) => value.trim().length > 0,
      },
    },

    // Description
    {
      id: "description",
      label: "Beschrijving",
      type: "textarea",
      icon: PencilSimple,
      getValue: (project) => project?.description || "",
      formatDisplay: (value) => value || "-",
      buildUpdatePayload: (value) => ({ description: value }),
      detailConfig: {
        editable: true,
        placeholder: "Voer project beschrijving in",
        section: "main",
      },
    },

    // Status
    {
      id: "status",
      label: "Status",
      type: "select",
      icon: SquaresFour,
      getValue: (project) => project?.status,
      formatDisplay: (value) => value || "-",
      buildUpdatePayload: (value) => ({ status: value }),
      detailConfig: {
        editable: true,
        section: "main",
      },
    },

    // Rough budget estimate — a top-down number captured early in the
    // project lifecycle, before the per-part / investment-line breakdown
    // exists. Displayed with the valuta mask (€ and locale formatting); the
    // editor is a plain number input. Stored excl. VAT on the project.
    // Becomes read-only once the budget is locked via "Goedkeuren &
    // vergrendelen" on the budget panel.
    {
      id: "roughBudgetEstimate",
      label: "Geschat ruw budget",
      type: "number",
      icon: Coins,
      getValue: (project) => {
        const v = project?.roughBudgetEstimate;
        return v === null || v === undefined || v === "" ? "" : Number(v);
      },
      formatDisplay: (value) => {
        if (value === null || value === undefined || value === "") return "-";
        return applyMask(Number(value), "valuta");
      },
      buildUpdatePayload: (value) => {
        // Empty input clears the estimate; numeric strings get coerced.
        if (value === null || value === undefined || value === "") {
          return { roughBudgetEstimate: null };
        }
        const parsed = Number(value);
        return {
          roughBudgetEstimate: Number.isFinite(parsed) ? parsed : null,
        };
      },
      detailConfig: {
        editable: true,
        section: "main",
        placeholder: "Bv. 250000",
        // Block edits while the budget is approved & locked. The
        // BudgetLockSection below the field offers an unlock affordance.
        getDisabled: (project: any) => !!project?.budgetLockedAt,
      },
    },

    // Building
    {
      id: "building",
      label: "Gebouw",
      type: "readonly",
      icon: Buildings,
      getValue: (project) => project?.building,
      formatDisplay: (value) => value?.id || "-",
      buildUpdatePayload: () => ({}), // Building cannot be changed
      detailConfig: {
        editable: false,
        section: "main",
        chipColor:
          "border-moss-primary/30 bg-moss-primary/10 text-moss-primary",
      },
    },

    // Responsible User
    {
      id: "responsibleUserId",
      label: "Projectverantwoordelijke",
      type: "picker",
      icon: User,
      pickerMode: "assignee",
      getValue: (project) => {
        // Extract user ID whether it's stored as string or object
        const userId = project?.responsibleUserId;

        // If it's an object (shouldn't happen, but handle it), extract the ID
        if (userId && typeof userId === "object" && userId.id) {
          return userId.id;
        }

        // Otherwise return the string ID or null
        return userId || null;
      },
      formatDisplay: (value) => {
        // Simple: just return the user ID as string
        // The customDisplayRenderer handles the actual avatar/name rendering
        return value || "-";
      },
      buildUpdatePayload: (value) => {
        // Extract user ID from value, whether it's a string or user object from picker
        let userId = value;

        // If value is an object (from UniversalPickerModal assignee mode), extract ID
        if (value && typeof value === "object" && value.id) {
          userId = value.id;
        }

        return { responsibleUserId: userId };
      },
      detailConfig: {
        editable: true,
        section: "main",
        chipColor:
          "border-brick-primary/30 bg-brick-primary/10 text-brick-primary",
        customDisplayRenderer: (project) => {
          // Extract user ID properly
          const userId = project?.responsibleUserId;
          const extractedId =
            userId && typeof userId === "object" && userId.id
              ? userId.id
              : userId;

          return <ResponsibleUserDisplay userId={extractedId} />;
        },
      },
      tableConfig: {
        renderCell: (project) => {
          // Extract user ID properly
          const userId = project?.responsibleUserId;
          const extractedId =
            userId && typeof userId === "object" && userId.id
              ? userId.id
              : userId;

          return <ResponsibleUserDisplay userId={extractedId} />;
        },
      },
    },

    // Geplande startdatum — user-set, editable while the planning is
    // unlocked. Once the planning is approved/locked it becomes the
    // immutable snapshot in `lockedPlannedStartDate`.
    {
      id: "plannedStartDate",
      label: "Geplande startdatum",
      type: "date",
      icon: CalendarDot,
      getValue: (project) => {
        // Prefer BE-computed initial planning (handles the locked snapshot).
        const initial =
          (project as any)?.initialPlannedStartDate ??
          (project as any)?.effectivePlannedStartDate;
        if (initial) {
          return typeof initial === "string"
            ? initial.split("T")[0]
            : new Date(initial).toISOString().split("T")[0];
        }
        const startDate = project?.dates?.find(
          (d: any) => d.type === "Verwachte startdatum" && !d.isDeprecated,
        )?.date;
        return startDate ? (typeof startDate === 'string' ? startDate.split('T')[0] : '') : "";
      },
      formatDisplay: (value) => {
        if (!value) return "-";
        return new Date(value).toLocaleDateString("nl-BE");
      },
      buildUpdatePayload: (value) => ({ plannedStartDate: value }),
      detailConfig: {
        // Backend rejects edits while the planning is locked
        // (BadRequestException → toast). FE shows the lock state in the
        // PlanningLockSection above.
        editable: true,
        section: "main",
      },
    },

    // Verwachte einddatum — same lock semantics as the start date.
    {
      id: "expectedEndDate",
      label: "Verwachte einddatum",
      type: "date",
      icon: CalendarDot,
      getValue: (project) => {
        const initial =
          (project as any)?.initialPlannedEndDate ??
          (project as any)?.effectivePlannedEndDate;
        if (initial) {
          return typeof initial === "string"
            ? initial.split("T")[0]
            : new Date(initial).toISOString().split("T")[0];
        }
        const endDate = project?.dates?.find(
          (d: any) => d.type === "Verwachte einddatum" && !d.isDeprecated,
        )?.date;
        return endDate ? (typeof endDate === 'string' ? endDate.split('T')[0] : '') : "";
      },
      formatDisplay: (value) => {
        if (!value) return "-";
        return new Date(value).toLocaleDateString("nl-BE");
      },
      buildUpdatePayload: (value) => ({ expectedEndDate: value }),
      detailConfig: {
        editable: true,
        section: "main",
      },
    },

    // Kostenplaatsen — single merged row. Shows the project's own picks plus
    // any unit already attached to a project part (rendered locked). Edit is
    // limited to project-only units; locked ones must be detached on the part
    // first.
    {
      id: "provisionalUnits",
      label: "Kostenplaatsen",
      type: "readonly",
      icon: SquaresFour,
      getValue: (project) => project?.provisionalUnits ?? [],
      formatDisplay: (value) => {
        if (!value || value.length === 0) return "Niet ingesteld";
        return `${value.length} units`;
      },
      buildUpdatePayload: () => ({}),
      detailConfig: {
        editable: false,
        section: "main",
        customDisplayRenderer: (project) => (
          <ProjectProvisionalUnitsCell project={project} />
        ),
      },
    },

  ],

  // ============ Feature Configurations ============
  emails: {
    enabled: false, // Disabled - Project Tasks Kanban takes this spot above details form
    fetchEmails: (entityId) =>
      `${API_URL}/email/renovation/project/${entityId}`,
    sendEmail: (entityId) =>
      `${API_URL}/email/renovation/project/${entityId}/send`,
    scope: "entity",
    relationConfig: {
      field: "projectId",
    },
  },

  documents: {
    enabled: true,
    sharePointConfig: {
      getFolderId: (project) => project?.sharePointFolderId,
      getFolderUrl: (project) => project?.sharePointFolderUrl,
    },
  },

  // ============ Navigation Configuration ============
  navigation: {
    backRoute: "/building", // Will be enhanced with building ID
    enableInterPageNav: false, // Projects are typically accessed from building view
    useFiltering: false,
  },

  // ============ Display Configuration ============
  titleField: "title",

  getMetadataChips: (project) => {
    const chips = [];

    // Status chip
    if (project?.status) {
      chips.push({
        label: project.status,
        icon: SquaresFour,
        color: "border-moss-primary/30 bg-moss-primary/10 text-moss-primary",
      });
    }

    // Building chip
    if (project?.building?.id) {
      chips.push({
        label: project.building.id,
        icon: Buildings,
        color: "border-neutral-400 bg-neutral-50 text-neutral-700",
      });
    }

    // Year chip (if available)
    if (project?.year) {
      chips.push({
        label: `${project.year}`,
        icon: Calendar,
        color: "border-neutral-400 bg-neutral-50 text-neutral-700",
      });
    }

    return chips;
  },

  loadingText: "Project laden...",
  notFoundText: "Project niet gevonden",

  // ============ Plugins & Customization ============
  plugins: {
    // Transform entity data after fetching
    transformEntityData: (data) => {
      return data;
    },

    // Custom sections - imported and configured in the detail page component
    customSections: [
      // Project parts section will be added dynamically
      // Documents section will be added dynamically
      // Emails section will be added dynamically
    ],

    // Custom actions
    actions: [],

    // Hook called after entity is loaded
    onEntityLoad: async (entity, _context) => {
      console.log("✅ Renovation project loaded:", entity);
    },

    // Hook called after entity is updated
    onEntityUpdate: async (entity, _context) => {
      console.log("✅ Renovation project updated:", entity);
    },
  },
};
